import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Card, Alert, Modal, Form, Row, Col, Pagination } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import {
  adminService,
  AdminAchievement,
  CreateAchievementAdmin,
  UpdateAchievementAdmin,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { ACHIEVEMENT_RARITY_API_VALUES, formatAchievementRarity } from '../utils/formatAchievementRarity';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import {
  ACHIEVEMENT_CRITERIA_TYPES,
  isKnownAchievementCriteriaType,
} from '../utils/achievementCriteriaTypes';

const emptyCreate: CreateAchievementAdmin = {
  nameEn: '',
  namePt: '',
  descriptionEn: '',
  descriptionPt: '',
  criteriaType: 'FIRST_QUIZ_COMPLETED',
  criteriaValue: 1,
  targetTag: '',
  themeEn: '',
  themePt: '',
  rarity: 'Common',
  points: 10,
  isSecret: false,
  iconAssetKey: '',
};

const AdminAchievements: React.FC = () => {
  const { t } = useSafeTranslation();
  const [items, setItems] = useState<AdminAchievement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterRarity, setFilterRarity] = useState<string>('');
  const [filterCriteria, setFilterCriteria] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('all');

  const ACH_ADMIN_PAGE_SIZE = 50;
  const [listPage, setListPage] = useState(1);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listHasNext, setListHasNext] = useState(false);
  const [listHasPrev, setListHasPrev] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState<AdminAchievement | null>(null);
  const [createForm, setCreateForm] = useState<CreateAchievementAdmin>(emptyCreate);
  const [editForm, setEditForm] = useState<UpdateAchievementAdmin | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<CreateAchievementAdmin[]>([]);
  const [importSubmitting, setImportSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters: {
        rarity?: string;
        criteriaType?: string;
        isActive?: boolean;
        page?: number;
        pageSize?: number;
      } = { page: listPage, pageSize: ACH_ADMIN_PAGE_SIZE };
      if (filterRarity) filters.rarity = filterRarity;
      if (filterCriteria.trim()) filters.criteriaType = filterCriteria.trim();
      if (filterActive === 'true') filters.isActive = true;
      if (filterActive === 'false') filters.isActive = false;
      const data = await adminService.getAchievements(filters);
      setItems(Array.isArray(data.achievements) ? data.achievements : []);
      setListTotalCount(data.totalCount);
      setListTotalPages(data.totalCount === 0 ? 1 : Math.max(1, data.totalPages));
      setListHasNext(data.hasNextPage);
      setListHasPrev(data.hasPreviousPage);
      setError(null);
    } catch {
      setError('Failed to load achievements');
    } finally {
      setIsLoading(false);
    }
  }, [filterRarity, filterCriteria, filterActive, listPage]);

  useEffect(() => {
    setListPage(1);
  }, [filterRarity, filterCriteria, filterActive]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (listTotalPages > 0 && listPage > listTotalPages) {
      setListPage(listTotalPages);
    }
  }, [listTotalPages, listPage]);

  const openEdit = (a: AdminAchievement) => {
    setSelected(a);
    setEditForm({
      nameEn: a.nameEn,
      namePt: a.namePt,
      descriptionEn: a.descriptionEn ?? '',
      descriptionPt: a.descriptionPt ?? '',
      criteriaType: a.criteriaType,
      criteriaValue: a.criteriaValue,
      targetTag: a.targetTag ?? '',
      themeEn: a.themeEn ?? '',
      themePt: a.themePt ?? '',
      rarity: String(a.rarity),
      points: a.points,
      isSecret: a.isSecret,
      iconAssetKey: a.iconAssetKey ?? '',
      isActive: a.isActive,
    });
    setShowEditModal(true);
  };

  const validateBilingual = (
    f: CreateAchievementAdmin | UpdateAchievementAdmin
  ): string | null => {
    if (!f.nameEn.trim() || !f.namePt.trim()) return 'Name (English and Portuguese) is required.';
    if (!f.descriptionEn.trim() || !f.descriptionPt.trim()) {
      return 'Description (English and Portuguese) is required.';
    }
    return null;
  };

  const handleCreate = async () => {
    const v = validateBilingual(createForm);
    if (v) {
      setError(v);
      return;
    }
    try {
      const payload: CreateAchievementAdmin = {
        ...createForm,
        nameEn: createForm.nameEn.trim(),
        namePt: createForm.namePt.trim(),
        descriptionEn: createForm.descriptionEn.trim(),
        descriptionPt: createForm.descriptionPt.trim(),
        targetTag: createForm.targetTag?.trim() || null,
        themeEn: createForm.themeEn?.trim() || null,
        themePt: createForm.themePt?.trim() || null,
        iconAssetKey: createForm.iconAssetKey?.trim() || null,
      };
      await adminService.createAchievement(payload);
      setShowCreateModal(false);
      setCreateForm(emptyCreate);
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to create achievement';
      setError(msg);
    }
  };

  const handleUpdate = async () => {
    if (!selected || !editForm) return;
    const v = validateBilingual(editForm);
    if (v) {
      setError(v);
      return;
    }
    try {
      const payload: UpdateAchievementAdmin = {
        ...editForm,
        nameEn: editForm.nameEn.trim(),
        namePt: editForm.namePt.trim(),
        descriptionEn: editForm.descriptionEn.trim(),
        descriptionPt: editForm.descriptionPt.trim(),
        targetTag: editForm.targetTag?.trim() || null,
        themeEn: editForm.themeEn?.trim() || null,
        themePt: editForm.themePt?.trim() || null,
        iconAssetKey: editForm.iconAssetKey?.trim() || null,
      };
      await adminService.updateAchievement(selected.id, payload);
      setShowEditModal(false);
      setSelected(null);
      setEditForm(null);
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update achievement';
      setError(msg);
    }
  };

  const handleDeactivate = async (a: AdminAchievement) => {
    try {
      await adminService.deactivateAchievement(a.id);
      await load();
    } catch {
      setError('Failed to deactivate');
    }
  };

  const handleActivate = async (a: AdminAchievement) => {
    try {
      await adminService.activateAchievement(a.id);
      await load();
    } catch {
      setError('Failed to activate');
    }
  };

  const rarityBadge = (r: string) => {
    const bg = r === 'Epic' ? 'warning' : r === 'Rare' ? 'info' : 'secondary';
    return <Badge bg={bg}>{formatAchievementRarity(r, t)}</Badge>;
  };

  const exportAchievementJson = (a: AdminAchievement) => {
    const payload = {
      id: a.id,
      nameEn: a.nameEn,
      namePt: a.namePt,
      descriptionEn: a.descriptionEn,
      descriptionPt: a.descriptionPt,
      criteriaType: a.criteriaType,
      criteriaValue: a.criteriaValue,
      targetTag: a.targetTag,
      themeEn: a.themeEn ?? null,
      themePt: a.themePt ?? null,
      rarity: a.rarity,
      points: a.points,
      isSecret: a.isSecret,
      iconAssetKey: a.iconAssetKey,
      isActive: a.isActive,
      createdAt: a.createdAt,
      updatedAt: a.updatedAt,
    };
    const jsonString = JSON.stringify(payload, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safe = a.nameEn.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').slice(0, 40) || 'achievement';
    link.download = `${safe}_${a.id.slice(0, 8)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const loadExampleAchievementsJson = async () => {
    try {
      const response = await fetch('/example-achievements.json');
      const data = await response.json();
      setImportJson(JSON.stringify(data, null, 2));
      setImportError(null);
      setImportPreview([]);
    } catch {
      setImportError(t('admin.achievementImportExampleError', 'Could not load example file'));
    }
  };

  const handleValidateAchievementImport = async () => {
    try {
      setImportError(null);
      const res = await adminService.validateAchievementsJson(importJson);
      setImportPreview(res.achievements ?? []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('admin.achievementImportValidateError', 'Validation failed');
      setImportError(msg);
      setImportPreview([]);
    }
  };

  const handleImportAchievementsConfirm = async () => {
    try {
      setImportSubmitting(true);
      setImportError(null);
      const res = await adminService.importAchievementsFromJson(importJson);
      toast.success(
        t('admin.achievementsImportedToast', 'Imported {{count}} achievement(s)').replace(
          '{{count}}',
          String(res.importedCount ?? res.achievements?.length ?? 0)
        )
      );
      setShowImportModal(false);
      setImportJson('');
      setImportPreview([]);
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('admin.achievementImportError', 'Import failed');
      setImportError(msg);
    } finally {
      setImportSubmitting(false);
    }
  };

  const formFields = (
    form: CreateAchievementAdmin | UpdateAchievementAdmin,
    setForm: (f: CreateAchievementAdmin | UpdateAchievementAdmin) => void,
    includeActive?: boolean
  ) => (
    <>
      <h6 className="text-muted mb-2">Player-facing text (required in both languages)</h6>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Name (English)</Form.Label>
            <Form.Control
              value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
              maxLength={200}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Name (Portuguese)</Form.Label>
            <Form.Control
              value={form.namePt}
              onChange={(e) => setForm({ ...form, namePt: e.target.value })}
              maxLength={200}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Description (English)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.descriptionEn}
              onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })}
              maxLength={2000}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Description (Portuguese)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.descriptionPt}
              onChange={(e) => setForm({ ...form, descriptionPt: e.target.value })}
              maxLength={2000}
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <h6 className="text-muted mb-2 mt-1">
        {t('admin.achievementThemeSection', 'Catalog theme (optional)')}
      </h6>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>{t('admin.achievementThemeEn', 'Theme (English)')}</Form.Label>
            <Form.Control
              value={form.themeEn ?? ''}
              onChange={(e) => setForm({ ...form, themeEn: e.target.value })}
              maxLength={120}
              placeholder={t('admin.achievementThemePlaceholder', 'e.g. Quizzes')}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>{t('admin.achievementThemePt', 'Theme (Portuguese)')}</Form.Label>
            <Form.Control
              value={form.themePt ?? ''}
              onChange={(e) => setForm({ ...form, themePt: e.target.value })}
              maxLength={120}
              placeholder={t('admin.achievementThemePtPlaceholder', 'ex.: Quizzes')}
            />
          </Form.Group>
        </Col>
      </Row>
      <p className="small text-muted mb-3">
        {t(
          'admin.achievementThemeHint',
          'Shown in the player catalog when set. Used for grouping or filtering; leave blank to omit.'
        )}
      </p>
      <Row>
        <Col md={8}>
          <Form.Group className="mb-2">
            <Form.Label>{t('admin.criteriaTypeLabel', 'Criteria type')}</Form.Label>
            <Form.Select
              value={form.criteriaType}
              onChange={(e) => setForm({ ...form, criteriaType: e.target.value })}
            >
              {!isKnownAchievementCriteriaType(form.criteriaType) && form.criteriaType.trim() !== '' && (
                <option value={form.criteriaType}>
                  {form.criteriaType} ({t('admin.criteriaTypeLegacy', 'legacy')})
                </option>
              )}
              {ACHIEVEMENT_CRITERIA_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`admin.criteriaType.${value}`, value)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Criteria value</Form.Label>
            <Form.Control
              type="number"
              value={form.criteriaValue}
              onChange={(e) =>
                setForm({ ...form, criteriaValue: parseInt(e.target.value, 10) || 0 })
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-2">
        <Form.Label>Target tag (required if type ends with _BY_TAG)</Form.Label>
        <Form.Control
          value={form.targetTag ?? ''}
          onChange={(e) => setForm({ ...form, targetTag: e.target.value })}
          maxLength={64}
        />
      </Form.Group>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>{t('achievements.rarity.fieldLabel', 'Rarity')}</Form.Label>
            <Form.Select
              value={form.rarity}
              onChange={(e) => setForm({ ...form, rarity: e.target.value })}
            >
              {ACHIEVEMENT_RARITY_API_VALUES.map((value) => (
                <option key={value} value={value}>
                  {formatAchievementRarity(value, t)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Points</Form.Label>
            <Form.Control
              type="number"
              value={form.points}
              onChange={(e) => setForm({ ...form, points: parseInt(e.target.value, 10) || 0 })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-2">
        <Form.Label>Icon file name (optional)</Form.Label>
        <Form.Control
          value={form.iconAssetKey ?? ''}
          onChange={(e) =>
            setForm({
              ...form,
              iconAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''),
            })
          }
          placeholder="e.g. achievement-1"
          maxLength={128}
        />
        <Form.Text className="text-muted">
          PNG in <code>public/assets/achievements/</code> — key <code>achievement-1</code> →{' '}
          <code>achievement-1.png</code>
        </Form.Text>
      </Form.Group>
      <Form.Check
        type="checkbox"
        className="mb-2"
        label="Secret achievement"
        checked={form.isSecret}
        onChange={(e) => setForm({ ...form, isSecret: e.target.checked })}
      />
      {includeActive && 'isActive' in form && (
        <Form.Check
          type="checkbox"
          className="mb-2"
          label="Active (visible in game when criteria met)"
          checked={(form as UpdateAchievementAdmin).isActive}
          onChange={(e) =>
            setForm({ ...(form as UpdateAchievementAdmin), isActive: e.target.checked })
          }
        />
      )}
    </>
  );

  return (
    <div className="admin-tab-content">
      <h3>Achievements</h3>
      <p className="text-muted">Manage achievement definitions (admin only).</p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small text-muted">
                {t('achievements.rarity.fieldLabel', 'Rarity')}
              </Form.Label>
              <Form.Select
                value={filterRarity}
                onChange={(e) => setFilterRarity(e.target.value)}
              >
                <option value="">{t('achievements.rarity.filterAll', 'All')}</option>
                {ACHIEVEMENT_RARITY_API_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {formatAchievementRarity(value, t)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small text-muted">Criteria type contains</Form.Label>
              <Form.Control
                value={filterCriteria}
                onChange={(e) => setFilterCriteria(e.target.value)}
                placeholder="e.g. QUIZ"
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted">Status</Form.Label>
              <Form.Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <option value="all">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button variant="primary" onClick={load} disabled={isLoading} className="w-100">
                {isLoading ? '…' : 'Apply'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end gap-2 flex-wrap mb-2">
        <Button variant="info" onClick={() => setShowImportModal(true)}>
          <i className="bi bi-upload me-2"></i>
          {t('admin.achievementImportJson', 'Import JSON')}
        </Button>
        <Button variant="success" onClick={() => setShowCreateModal(true)}>
          {t('admin.achievementNew', 'New achievement')}
        </Button>
      </div>

      <Card>
        <Card.Body>
          {listTotalCount === 0 && !isLoading ? (
            <p className="text-muted mb-0">No achievements match the filters.</p>
          ) : (
            <>
            <Table responsive striped hover size="sm">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Names (EN / PT)</th>
                  <th>Criteria</th>
                  <th>{t('achievements.rarity.fieldLabel', 'Rarity')}</th>
                  <th>Pts</th>
                  <th>Secret</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(items) ? items : []).map((a) => {
                  const thumb = achievementIconSrcFromKey(a.iconAssetKey);
                  return (
                  <tr key={a.id}>
                    <td className="align-middle">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          width={40}
                          height={40}
                          className="rounded-circle border"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                      {a.iconAssetKey ? (
                        <div>
                          <code className="small">{a.iconAssetKey}</code>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="small">
                        <strong>EN:</strong> {a.nameEn}
                      </div>
                      <div className="small text-muted">
                        <strong>PT:</strong> {a.namePt}
                      </div>
                      {(a.themeEn || a.themePt) && (
                        <div className="small text-muted mt-1">
                          <strong>{t('admin.achievementThemeShort', 'Theme')}:</strong>{' '}
                          {a.themeEn || '—'} <span className="text-body-secondary">/</span> {a.themePt || '—'}
                        </div>
                      )}
                    </td>
                    <td>
                      <small>
                        {a.criteriaType}
                        {a.criteriaValue != null && ` = ${a.criteriaValue}`}
                        {a.targetTag && ` @ ${a.targetTag}`}
                      </small>
                    </td>
                    <td>{rarityBadge(String(a.rarity))}</td>
                    <td>{a.points}</td>
                    <td>{a.isSecret ? 'Yes' : '—'}</td>
                    <td>
                      <Badge bg={a.isActive ? 'success' : 'secondary'}>
                        {a.isActive ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="text-end text-nowrap">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-1"
                        onClick={() => exportAchievementJson(a)}
                        title={t('admin.achievementExportJson', 'Export JSON')}
                      >
                        <i className="bi bi-download me-1"></i>
                        {t('admin.achievementExportJson', 'Export JSON')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => openEdit(a)}
                      >
                        {t('common.edit', 'Edit')}
                      </Button>
                      {a.isActive ? (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() => handleDeactivate(a)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => handleActivate(a)}
                        >
                          Activate
                        </Button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
            {listTotalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination className="mb-0 flex-wrap justify-content-center">
                  <Pagination.First
                    disabled={!listHasPrev}
                    onClick={() => setListPage(1)}
                  />
                  <Pagination.Prev
                    disabled={!listHasPrev}
                    onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: Math.min(5, listTotalPages) }, (_, i) => {
                    const pageNum =
                      Math.max(1, Math.min(listTotalPages - 4, listPage - 2)) + i;
                    if (pageNum > listTotalPages) return null;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === listPage}
                        onClick={() => setListPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={!listHasNext}
                    onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
                  />
                  <Pagination.Last
                    disabled={!listHasNext}
                    onClick={() => setListPage(listTotalPages)}
                  />
                </Pagination>
              </div>
            )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>New achievement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formFields(createForm, (f) => setCreateForm(f as CreateAchievementAdmin))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit achievement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editForm &&
            formFields(editForm, (f) => setEditForm(f as UpdateAchievementAdmin), true)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.achievementImportTitle', 'Import achievements from JSON')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <h6 className="mb-2">{t('admin.achievementImportFormatTitle', 'JSON format')}</h6>
            <p className="mb-2 small">
              {t(
                'admin.achievementImportFormatBody',
                'Provide a single achievement object or an array. Required fields: nameEn, namePt, descriptionEn, descriptionPt, criteriaType, criteriaValue, rarity, points, isSecret. Optional: targetTag, iconAssetKey, themeEn, themePt (max 120 chars each). Property names are case-insensitive.'
              )}
            </p>
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.achievementImportDataLabel', 'JSON data')}</Form.Label>
            <div className="mb-2">
              <Button variant="outline-secondary" size="sm" onClick={loadExampleAchievementsJson} className="me-2">
                <i className="bi bi-file-earmark-arrow-down me-1"></i>
                {t('admin.achievementImportLoadExample', 'Load example')}
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  setImportJson('');
                  setImportError(null);
                  setImportPreview([]);
                }}
              >
                <i className="bi bi-trash me-1"></i>
                {t('admin.achievementImportClear', 'Clear')}
              </Button>
            </div>
            <Form.Control
              as="textarea"
              rows={12}
              value={importJson}
              onChange={(e) => {
                setImportJson(e.target.value);
                if (importError) setImportError(null);
              }}
              spellCheck={false}
            />
          </Form.Group>
          {importError && (
            <Alert variant="danger">
              <strong>{t('messages.validationError', 'Validation error')}:</strong> {importError}
            </Alert>
          )}
          {importPreview.length > 0 && (
            <Alert variant="success">
              {t('admin.achievementImportReady', 'Validation OK: {{count}} achievement(s) will be created.').replace(
                '{{count}}',
                String(importPreview.length)
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            {t('admin.achievementImportCancel', 'Cancel')}
          </Button>
          <Button variant="outline-primary" onClick={handleValidateAchievementImport} disabled={!importJson.trim()}>
            {t('admin.achievementImportValidate', 'Validate')}
          </Button>
          <Button
            variant="primary"
            onClick={handleImportAchievementsConfirm}
            disabled={!importJson.trim() || importSubmitting}
          >
            {importSubmitting ? '…' : t('admin.achievementImportRun', 'Import')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminAchievements;
